<template>
  <v-container>
    <v-row>
      <v-col>
        <v-breadcrumbs
          :items="breadcrumbs"
          class="pa-0"
          large
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <wolk-private-data-alert />
      </v-col>
    </v-row>

    <template v-if="!loading">
      <v-row>
        <v-col>
          <h2>Organisatie {{ organization.title }}</h2>
        </v-col>
      </v-row>

      <analytics-v3-block
        scope="organization"
        :scope-id="$route.params.id"
      />

      <monthly-statistics-block
        v-if="showMonthlyStatistics"
        scope="organization"
        :scope-id="$route.params.id"
      />

      <v-row>
        <v-col>
          <v-card>
            <v-data-table
              :headers="userHeaders"
              :items="users"
              :items-per-page="20"
              :footer-props="{ itemsPerPageOptions: [10, 20, 50, 200] }"
              :loading="loading"
            >
              <template #top>
                <v-toolbar flat>
                  <v-toolbar-title>Gebruikers</v-toolbar-title>
                </v-toolbar>
              </template>

              <template #[`item.type`]="{ item }">
                {{ getHumanizedUserType(item.type) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <wolk-statuses-block
        v-if="analyticsData"
        :analytics-data="analyticsData"
      />

      <v-row>
        <v-col>
          <v-card>
            <v-data-table
              :headers="wolkHeaders"
              :options.sync="wolkTableSort"
              :items="wolks"
              :items-per-page="20"
              :footer-props="{ itemsPerPageOptions: [10, 20, 50, 200] }"
              :loading="loading"
            >
              <template #top>
                <v-toolbar flat>
                  <v-toolbar-title>Wolks</v-toolbar-title>
                </v-toolbar>
              </template>

              <template #[`item.metaData.sleevePublicId`]="{ item }">
                <router-link :to="{ name: 'wolksShow', params: { id: item._id } }">
                  {{ item.metaData.sleevePublicId }}
                </router-link>
              </template>

              <template #[`item.metaData.locationTitle`]="{ item }">
                <router-link
                  v-if="item.metaData && item.metaData.locationTitle"
                  :to="{ name: 'locationsShow', params: { id: item.locationId } }"
                >
                  {{ item.metaData.locationTitle }}
                </router-link>
              </template>

              <template #[`item.metaData.size`]="{ item }">
                {{ item.metaData.size }}
              </template>

              <template #[`item.currentStatusInfo.batteryPercentage`]="{ item }">
                <v-chip
                  :color="getHumanizedBatteryPercentage(item).color"
                >
                  {{ getHumanizedBatteryPercentage(item).name }}
                </v-chip>
              </template>

              <template #[`item.currentStatusInfo.firmwareVersion`]="{ item }">
                <firmware-version-chip :status-info="item.currentStatusInfo || {}" />
              </template>

              <template #[`item.currentStatusInfo.parsedStatusKey`]="{ item }">
                <v-chip
                  :color="getStatusInfo(item.currentStatus).color"
                  :text-color="getStatusInfo(item.currentStatus).inversTextColor
                    ? 'white'
                    : 'black'
                  "
                >
                  {{ getStatusInfo(item.currentStatus).title }}
                </v-chip>
              </template>

              <template #[`item.currentStatusInfo.createdAt`]="{ item }">
                {{
                  item.currentStatusInfo ?
                    getHumanizedDate(item.currentStatusInfo.createdAt) :
                    '--'
                }}
              </template>
              <template #[`item.retour.status`]="{ item }">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ getIconForRetourStatus(item.retour) }}
                    </v-icon>
                  </template>
                  <span>{{ getHumanizedRetourStatus(item.retour) }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import axios from 'axios';

import AnalyticsV3Block from '../../components/AnalyticsV3Block.vue';
import FirmwareVersionChip from '../../components/FirmwareVersionChip.vue';
import MonthlyStatisticsBlock from '../../components/MonthlyStatisticsBlock.vue';
import WolkPrivateDataAlert from '../../components/WolkPrivateDataAlert.vue';
import WolkStatusesBlock from '../../components/WolkStatusesBlock.vue';

import {
  getHumanizedDate,
  getHumanizedBatteryPercentage,
  getStatusInfo,
  createBreadcrumbs,
  getHumanizedUserType,
} from '../../helpers/humanizer';

export default {
  components: {
    AnalyticsV3Block,
    FirmwareVersionChip,
    MonthlyStatisticsBlock,
    WolkPrivateDataAlert,
    WolkStatusesBlock,
  },
  data() {
    return {
      loading: false,
      organization: {
        title: '',
      },
      users: [],
      wolks: [],
      analyticsData: null,
      userHeaders: [
        { text: 'Voornaam', align: 'start', value: 'firstName' },
        { text: 'Achternaam', value: 'lastName' },
        { text: 'Email', value: 'email' },
        { text: 'Type', value: 'type' },
      ],
      wolkTableSort: {
        page: 1,
        itemsPerPage: 20,
        sortBy: ['currentStatusInfo.createdAt'],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: false,
      },
    };
  },
  computed: {
    breadcrumbs() {
      return createBreadcrumbs(
        this.$store.state.app.dashboardHomeName,
        [
          {
            text: 'Organisaties',
            disabled: false,
            exact: true,
            to: { name: 'organizations' },
          },
          {
            text: this.organization.title,
            disabled: true,
            exact: true,
            to: { name: 'organizations', params: { id: this.$route.params.id } },
          },
        ],
        false,
      );
    },
    showMonthlyStatistics() {
      return this.$store.getters['auth/isAdmin'];
    },
    wolkHeaders() {
      if (this.$store.state.auth.type === 'admin') {
        return [
          { text: 'Wolknr.', value: 'metaData.sleevePublicId', sortable: false },
          { text: 'ID', value: 'customerIdentifier', sortable: false },
          { text: 'Locatie', value: 'metaData.locationTitle', sortable: false },
          { text: 'Type', value: 'type', sortable: true },
          { text: 'Maat', value: 'metaData.size' },
          { text: 'Retour', value: 'retour.status', sortable: true },
          { text: 'Status', value: 'currentStatusInfo.parsedStatusKey' },
          { text: 'Batterij', value: 'currentStatusInfo.batteryPercentage' },
          { text: 'Software', value: 'currentStatusInfo.firmwareVersion' },
          { text: 'Laatst online', value: 'currentStatusInfo.createdAt' },
        ];
      }

      if (this.organization?.retourEnabled) {
        return [
          { text: 'Wolknr.', value: 'metaData.sleevePublicId', sortable: false },
          { text: 'ID', value: 'customerIdentifier', sortable: false },
          { text: 'Locatie', value: 'metaData.locationTitle', sortable: false },
          { text: 'Type', value: 'type', sortable: true },
          { text: 'Maat', value: 'metaData.size' },
          { text: 'Retour', value: 'retour.status', sortable: true },
          { text: 'Status', value: 'currentStatusInfo.parsedStatusKey' },
          { text: 'Batterij', value: 'currentStatusInfo.batteryPercentage' },
          { text: 'Software', value: 'currentStatusInfo.firmwareVersion' },
          { text: 'Laatst online', value: 'currentStatusInfo.createdAt' },
        ];
      }

      return [
        { text: 'Wolknr.', value: 'metaData.sleevePublicId', sortable: false },
        { text: 'ID', value: 'customerIdentifier', sortable: false },
        { text: 'Locatie', value: 'metaData.locationTitle', sortable: false },
        { text: 'Type', value: 'type', sortable: true },
        { text: 'Maat', value: 'metaData.size' },
        { text: 'Status', value: 'currentStatusInfo.parsedStatusKey' },
        { text: 'Batterij', value: 'currentStatusInfo.batteryPercentage' },
        { text: 'Software', value: 'currentStatusInfo.firmwareVersion' },
        { text: 'Laatst online', value: 'currentStatusInfo.createdAt' },
      ];
    },
  },
  beforeMount() {
    this.loadOrganization();
  },
  methods: {
    getHumanizedDate(millis) {
      return getHumanizedDate(millis);
    },
    getHumanizedBatteryPercentage(wolk) {
      return getHumanizedBatteryPercentage(wolk, true);
    },
    getStatusInfo(key) {
      return getStatusInfo(key);
    },
    getHumanizedUserType(key) {
      return getHumanizedUserType(key);
    },
    loadOrganization() {
      const url = `${config.VUE_APP_API_BASE_URL}/organizations/${this.$route.params.id}`;

      this.loading = true;

      axios({
        url,
        headers: {
          Authorization: `Bearer ${this.$store.state.auth.jwt}`,
        },
        method: 'get',
      })
        .then((response) => {
          this.organization = response.data.organization;
          this.analyticsData = response.data.analyticsData;
          this.users = response.data.users;
          this.wolks = response.data.wolks;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getHumanizedRetourStatus(retour) {
      const status = retour?.status;
      const retourReason = retour?.reason;

      if (status === 'exchange') {
        if (retourReason === 'maintenance') {
          return 'onderhoud - omruilen';
        }
        return 'omruilen';
      }
      if (status === 'sent') {
        return 'verzonden';
      }
      if (status === 'maintenance') {
        return 'onderhoud';
      }
      if (status === 'retour') {
        return 'retour';
      }
      return '';
    },
    getIconForRetourStatus(retour) {
      const retourStatus = retour?.status;
      const retourReason = retour?.reason;

      if (retourStatus === 'sent') {
        return 'mdi-email-fast-outline';
      }
      if (retourStatus === 'maintenance') {
        return 'mdi-wrench';
      }
      if (retourStatus === 'exchange') {
        if (retourReason === 'maintenance') {
          return 'mdi-wrench';
        }
        return 'mdi-swap-horizontal';
      }
      if (retourStatus === 'retour') {
        return 'mdi-arrow-right';
      }
      return '';
    },
  },
};
</script>
